import axios from "axios";
import Cookies from "js-cookie";
import { BASE_URL } from "../constans/ApiEndpointConstants.js";

// Creates api instance to be used across the project
// and set appropriate default values
//
// There is a bug in which axios leaks instance headers to the global
// axios object. There is a workaround which is using a deep object in the
// instance initialization.
// https://github.com/axios/axios/issues/1664

const api = axios.create({
  baseURL: BASE_URL,
  headers: {
    common: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
  },
  withCredentials: false, // 启用跨域请求时携带 Cookie
  timeout: 10000,
});

// Create aliases
api.create = api.post;
api.update = api.put;

// ✅ 请求拦截器：自动从 Cookie、localStorage 或默认 headers 中获取 token
api.interceptors.request.use(
  function (config) {
    // 如果请求配置中标记了 skipAuth，则跳过 token 添加（用于登录等不需要认证的请求）
    if (config.skipAuth) {
      return config;
    }

    // 优先使用默认 headers 中设置的 token（通过 setAPIAuthorizationToken 设置）
    if (api.defaults.headers.common["token"]) {
      // token 已经在 headers 中，不需要额外处理
    } else {
      // 如果没有手动设置，优先从 Cookie 读取，其次从 localStorage 读取
      const token = Cookies.get("token") || localStorage.getItem("token");
      if (token) {
        // 后端优先支持 "token" 请求头（优先级高于 Authorization）
        config.headers.token = token;
        // 同时也设置标准的 Authorization 头（兼容性）
        config.headers.Authorization = `Bearer ${token}`;
      }
    }

    return config;
  },
  function (error) {
    return Promise.reject(error);
  }
);

// ✅ 响应拦截器：统一处理响应和错误
api.interceptors.response.use(
  function (response) {
    return response;
  },
  function (error) {
    if (error.response && error.response.status === 401) {
      // Token 过期或无效
      // 清除 Cookie 和 localStorage 中的 token
      Cookies.remove("token");
      Cookies.remove("user");
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      unsetAPIAuthorizationToken();

      // 重定向到登录页面
      if (window.location.pathname !== "/") {
        window.location.href = "/";
      }
      return Promise.reject(error);
    } else {
      return Promise.reject(error);
    }
  }
);

// ✅ Sets authorization header. Only to be used in log in
export function setAPIAuthorizationToken(accessToken) {
  // 后端优先支持 "token" 请求头（自定义方式）
  api.defaults.headers.common["token"] = `${accessToken}`;
  // 同时也设置标准的 Authorization 头（兼容性）
  api.defaults.headers.common["Authorization"] = `Bearer ${accessToken}`;
  // 同步到 Cookie 和 localStorage（保持一致性）
  Cookies.set("token", accessToken, { expires: 7 });
  localStorage.setItem("token", accessToken);
}

// ✅ Unsets authorization header. Only to be used in log out
export function unsetAPIAuthorizationToken() {
  delete api.defaults.headers.common["token"];
  delete api.defaults.headers.common["Authorization"];
  // 清除 Cookie 和 localStorage
  Cookies.remove("token");
  Cookies.remove("user");
  localStorage.removeItem("token");
  localStorage.removeItem("user");
}

// ✅ Sets base api url. Only to be used in dev when changing environment
export function setAPIBaseUrl(baseURL) {
  api.defaults.baseURL = baseURL;
}

export default api;
